import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingBag, Check } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import StructuredData from "../components/StructuredData";

export default function ProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const [product, setProduct] = useState(null);
    const [size, setSize] = useState(null);
    const [qty, setQty] = useState(1);
    const [added, setAdded] = useState(false);

    useEffect(() => {
        api.get(`/products/${slug}`).then((r) => {
            setProduct(r.data);
            if (r.data.sizes?.length) setSize(r.data.sizes[Math.floor(r.data.sizes.length / 2)]);
        }).catch(() => navigate("/store"));
    }, [slug, navigate]);

    if (!product) return null;
    const hasSizes = product.sizes && product.sizes.length > 0;

    const handleAdd = () => {
        if (hasSizes && !size) { toast.error("Please pick a size."); return; }
        addItem(product, { size, quantity: qty });
        setAdded(true);
        toast.success(`Added to cart: ${product.name}`);
        setTimeout(() => setAdded(false), 1800);
    };

    const handleBuyNow = () => {
        if (hasSizes && !size) { toast.error("Please pick a size."); return; }
        addItem(product, { size, quantity: qty });
        navigate("/store/checkout");
    };

    return (
        <div data-testid="product-page" className="grain">
            <StructuredData data={{
                "@context": "https://schema.org",
                "@type": "Product",
                "name": product.name,
                "description": product.description,
                "image": product.image_url ? `https://purplekingshoops.com${product.image_url}` : undefined,
                "brand": { "@type": "Brand", "name": "Purple Kings" },
                "category": product.category,
                "sku": product.slug,
                "offers": {
                    "@type": "Offer",
                    "price": (product.price_cents / 100).toFixed(2),
                    "priceCurrency": "USD",
                    "availability": product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                    "url": `https://purplekingshoops.com/store/${product.slug}`,
                    "seller": { "@type": "Organization", "name": "Purple Kings" },
                },
            }} />
            <section className="max-w-7xl mx-auto px-5 md:px-10 py-10 md:py-16">
                <Link to="/store" className="inline-flex items-center gap-2 text-ink hover:text-gold text-xs tracking-[0.3em] uppercase font-bold mb-8">
                    <ArrowLeft size={14} /> Back to Store
                </Link>
                <div className="grid md:grid-cols-2 gap-10">
                    <div className="card-flat bg-bg-soft aspect-square relative overflow-hidden">
                        <div
                            className="absolute inset-0 bg-contain bg-center bg-no-repeat"
                            style={{ backgroundImage: `url('${product.image_url}')` }}
                            data-testid="product-image"
                        />
                    </div>
                    <div>
                        <div className="text-gold text-[10px] tracking-[0.3em] uppercase font-bold">{product.category}</div>
                        <h1 className="h-display text-4xl md:text-5xl mt-3 leading-tight" data-testid="product-name">{product.name}</h1>
                        <div className="text-3xl text-gold font-bold mt-4">${(product.price_cents / 100).toFixed(2)}</div>
                        <p className="text-white/80 text-base mt-6 leading-relaxed">{product.description}</p>

                        {hasSizes && (
                            <div className="mt-8">
                                <div className="text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-3">Size</div>
                                <div className="flex flex-wrap gap-2" data-testid="size-options">
                                    {product.sizes.map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            onClick={() => setSize(s)}
                                            data-testid={`size-${s}`}
                                            className={`px-4 py-2 text-sm font-bold border transition-colors ${
                                                size === s
                                                    ? "border-gold bg-gold text-bg"
                                                    : "border-line text-white hover:border-gold"
                                            }`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mt-8">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-3">Quantity</div>
                            <div className="inline-flex items-center border border-line" data-testid="qty-control">
                                <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-3 hover:text-gold" aria-label="decrease"><Minus size={14} /></button>
                                <div className="px-5 font-bold" data-testid="qty-value">{qty}</div>
                                <button type="button" onClick={() => setQty((q) => q + 1)} className="px-4 py-3 hover:text-gold" aria-label="increase"><Plus size={14} /></button>
                            </div>
                        </div>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <button type="button" onClick={handleAdd} data-testid="add-to-cart" className="btn-ghost-gold px-6 py-3 text-xs inline-flex items-center gap-2">
                                {added ? <><Check size={14} /> Added</> : <><ShoppingBag size={14} /> Add to Cart</>}
                            </button>
                            <button type="button" onClick={handleBuyNow} data-testid="buy-now" className="btn-gold px-6 py-3 text-xs">
                                Buy Now
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
